import { Injectable } from '@angular/core';
import { Firestore, doc, updateDoc, setDoc, deleteDoc, collection, query, where, getDocs, writeBatch, serverTimestamp, addDoc } from '@angular/fire/firestore';
import { User, LemmaState, Attempt, Session } from '../entities/user.model';
import { AuthService } from './auth.service';
import { FlashcardService } from './flashcard.service';
import { UserLookupService } from './user-lookup.service';
import { Flashcard, WordCard } from '../entities/wordcard.model';
import { firstValueFrom } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class UserService {

  public touring: boolean = false; // Flag to indicate if the user is currently touring

  constructor(private firestore: Firestore,
    private authService: AuthService,
    private flashCardService: FlashcardService,
    private userLookup: UserLookupService
  ) {}

  async applyChanges(user: User): Promise<void> {
    const userDocRef = doc(this.firestore, `users/${user.uid}`);
    const updateData: any = {
      nativeLanguage: user.nativeLanguage,
      targetLanguage: user.targetLanguage,
      level: user.level
    };

    // Only include optional fields if they are defined
    if (user.textSize !== undefined) {
      updateData.textSize = user.textSize;
    }
    if (user.darkMode !== undefined) {
      updateData.darkMode = user.darkMode;
    }

    await updateDoc(userDocRef, updateData);

    this.authService.setUser(user);
  }

  async updateDisplayName(displayName: string): Promise<void> {
    const user = this.authService.currentUserValue;
    if (!user || !user.uid) throw new Error('No user logged in');
    const userDocRef = doc(this.firestore, `users/${user.uid}`);
    await updateDoc(userDocRef, { displayName });
    await this.userLookup.writeUsername(user.uid, displayName);
    user.displayName = displayName;
    this.authService.setUser(user);
  }

  checkIfWordInVocab(word: string, languageCode: string): boolean {
    const user = this.authService.currentUserValue;
    if (!user || !user.vocab || !user.vocab[languageCode]) {
      return false;
    }
    const wordsMap = user.vocab[languageCode].words;
    return Object.keys(wordsMap).includes(word);
  }
  // addWordToVocab which takes in a WordCard, creates a Flashcard for it and adds it to the user's vocabulary
  async addWordToVocab(wordCard: WordCard, targetLanguage: string, articleId: string): Promise<string> {
    const user = this.authService.currentUserValue;
    if (!user) return "notSignedIn";
    if (!user.email) return "temporaryUser";
    try {
      if (!user.vocab) {
        user.vocab = {};
      }
      if (!user.vocab[targetLanguage]) {
        user.vocab[targetLanguage] = { words: {} };
      }
      // Create a Flashcard from the WordCard
      const flashCard: Flashcard | null = await this.flashCardService.createFlashCard(wordCard, articleId, user);
      if (!flashCard || !flashCard.id) {
        return "flashCardError";
      }
      // Add the Flashcard to the user's vocabulary
      if (!user.vocab[targetLanguage].words[wordCard.root_word]) {
        user.vocab[targetLanguage].words[wordCard.root_word] = [flashCard.id];
      } else {
        user.vocab[targetLanguage].words[wordCard.root_word] = [
          ...user.vocab[targetLanguage].words[wordCard.root_word],
          flashCard.id
        ];
      }
      // Update the user document in Firestore
      const userDocRef = doc(this.firestore, `users/${user.uid}`);
      await updateDoc(userDocRef, {
        [`vocab.${targetLanguage}.words.${wordCard.root_word}`]: user.vocab[targetLanguage].words[wordCard.root_word]
      });
      return "success";
    } catch (error) {
      return "flashCardError";
    }
  }

  async updateUserTouringStatus(): Promise<void> {
    this.touring = false;
    const user = this.authService.currentUserValue;
    if (user) {
      user.completedTour = true; // Set completedTour to true
      const userDocRef = doc(this.firestore, `users/${user.uid}`);
      await updateDoc(userDocRef, { completedTour: true });
      this.authService.getCurrentUser(); // Refresh the current user
    }
  }

  async toggleBookMark(articleId: string, add: boolean): Promise<void> {
    const user = this.authService.currentUserValue;
    if (!user) {
      throw new Error('No user logged in');
    }
    if (!user.bookmarks) {
      user.bookmarks = [];
    }

    if (add) {
      if (!user.bookmarks.includes(articleId)) {
        user.bookmarks.push(articleId);
      }
    } else {
      user.bookmarks = user.bookmarks.filter(id => id !== articleId);
    }

    const userDocRef = doc(this.firestore, `users/${user.uid}`);
    await updateDoc(userDocRef, { bookmarks: user.bookmarks });
  }

  async togglePinnedPlaylist(playlistId: string, pin: boolean): Promise<void> {
    const user = this.authService.currentUserValue;
    if (!user) throw new Error('No user logged in');
    if (!user.pinnedPlaylists) user.pinnedPlaylists = [];

    if (pin) {
      if (!user.pinnedPlaylists.includes(playlistId)) {
        user.pinnedPlaylists.push(playlistId);
      }
    } else {
      user.pinnedPlaylists = user.pinnedPlaylists.filter(id => id !== playlistId);
    }

    const userDocRef = doc(this.firestore, `users/${user.uid}`);
    await updateDoc(userDocRef, { pinnedPlaylists: user.pinnedPlaylists });
    this.authService.setUser(user);
  }

  async deleteUserAccount(): Promise<void> {
    const user = this.authService.currentUserValue;
    if (!user || !user.uid) {
      throw new Error('No user logged in');
    }

    try {
      // Delete all user's flashcards
      const flashcardsQuery = query(
        collection(this.firestore, 'flash_cards'),
        where('userId', '==', user.uid)
      );
      
      const flashcardsDocs = await getDocs(flashcardsQuery);
      const batch = writeBatch(this.firestore);
      
      flashcardsDocs.forEach((doc) => {
        batch.delete(doc.ref);
      });
      
      // Delete the user document
      const userDocRef = doc(this.firestore, `users/${user.uid}`);
      batch.delete(userDocRef);
      
      // Commit all deletions
      await batch.commit();
      
      console.log('User account and all related data deleted successfully');
    } catch (error) {
      console.error('Error deleting user account:', error);
      throw error;
    }
  }

  async saveLemmaStates(lemmaStates: LemmaState[]): Promise<void> {
    const user = this.authService.currentUserValue;
    if (!user || !user.uid) {
      throw new Error('No user logged in');
    }

    try {
      const batch = writeBatch(this.firestore);
      
      for (const lemmaState of lemmaStates) {
        const docRef = doc(this.firestore, `users/${user.uid}/lemmaStates/${lemmaState.lemmaId}`);
        batch.set(docRef, lemmaState);
      }
      
      await batch.commit();
      console.log(`Saved ${lemmaStates.length} lemma states to Firestore`);
    } catch (error) {
      console.error('Error saving lemma states:', error);
      throw error;
    }
  }

  async getUnknownUnsure(): Promise<LemmaState[]> {
    const user = this.authService.currentUserValue;
    if (!user || !user.uid) {
      throw new Error('No user logged in');
    }

    try {
      // Query for lemma states with wordStatus 0 (unknown) or 1 (unsure) directly from Firestore
      const lemmaStatesRef = collection(this.firestore, `users/${user.uid}/lemmaStates`);
      const q = query(lemmaStatesRef, where('wordStatus', 'in', [0, 1]));
      const lemmaStatesSnapshot = await getDocs(q);
      
      const unknownUnsureLemmas: LemmaState[] = [];
      lemmaStatesSnapshot.forEach(doc => {
        unknownUnsureLemmas.push(doc.data() as LemmaState);
      });

      return unknownUnsureLemmas;
    } catch (error) {
      console.error('Error fetching unknown/unsure lemmas:', error);
      throw error;
    }
  }

  /**
   * Get all lemma states for the current user
   * @returns Promise<LemmaState[]> All lemma states
   */
  async getLemmas(): Promise<LemmaState[]> {
    const user = this.authService.currentUserValue;
    if (!user || !user.uid) {
      throw new Error('No user logged in');
    }

    try {
      // Query for all lemma states from Firestore
      const lemmaStatesRef = collection(this.firestore, `users/${user.uid}/lemmaStates`);
      const lemmaStatesSnapshot = await getDocs(lemmaStatesRef);

      const allLemmas: LemmaState[] = [];
      lemmaStatesSnapshot.forEach(doc => {
        allLemmas.push(doc.data() as LemmaState);
      });

      return allLemmas;
    } catch (error) {
      console.error('Error fetching all lemmas:', error);
      throw error;
    }
  }

  async changeLemmaStatus(lemmaId: string, newStatus: number): Promise<void> {
    const user = this.authService.currentUserValue;
    if (!user || !user.uid) {
      throw new Error('No user logged in');
    }

    try {
      const lemmaStateRef = doc(this.firestore, `users/${user.uid}/lemmaStates/${lemmaId}`);
      await setDoc(lemmaStateRef, { wordStatus: newStatus, updatedAt: serverTimestamp() }, { merge: true });
      console.log(`Updated lemma ${lemmaId} to new status ${newStatus}`);
    } catch (error) {
      console.error('Error updating lemma status:', error);
      throw error;
    }
  }

  /**
   * Skip a word during introduction phase.
   * Updates the lemma state to mark as skipped (wordStatus = 3).
   */
  async skipWordFromMasteryQueue(lemmaId: string): Promise<void> {
    const user = this.authService.currentUserValue;
    if (!user || !user.uid) {
      throw new Error('No user logged in');
    }

    try {
      await this.changeLemmaStatus(lemmaId, 3); // 3 = skipped

      console.log(`Skipped word ${lemmaId} and marked as skipped in lemma state`);
    } catch (error) {
      console.error('Error skipping word:', error);
      throw error;
    }
  }

  /**
   * Accept a word during introduction phase.
   * Adds the word to masteryQueue and updates the user document in Firestore.
   */
  async acceptWordFromMasteryQueue(lemmaId: string): Promise<void> {
    const user = this.authService.currentUserValue;
    if (!user || !user.uid) {
      throw new Error('No user logged in');
    }

    try {
      await this.changeLemmaStatus(lemmaId, 4); // 4 = studying (accepted but not yet mastered)

      // Initialize masteryQueue if it doesn't exist
      if (!user.masteryQueue) {
        user.masteryQueue = [];
      }

      // Add word to masteryQueue
      user.masteryQueue.push(lemmaId);

      // Update user document with new masteryQueue
      const userDocRef = doc(this.firestore, `users/${user.uid}`);
      await updateDoc(userDocRef, {
        masteryQueue: user.masteryQueue
      });

      console.log(`Accepted word ${lemmaId} and added to mastery queue`);
    } catch (error) {
      console.error('Error accepting word to mastery queue:', error);
      throw error;
    }
  }

  /**
   * Set the learning rate for the user in Firestore
   */
  async setLearningRate(learningRate: number): Promise<void> {
    const user = this.authService.currentUserValue;
    if (!user || !user.uid) {
      throw new Error('No user logged in');
    }

    try {
      // Update the user document with the learning rate and transition to learning phase
      const userDocRef = doc(this.firestore, `users/${user.uid}`);
      await updateDoc(userDocRef, {
        learningRate: learningRate,
        learningPhase: 'learning'
      });

      // Update the local user object
      user.learningRate = learningRate;
      user.learningPhase = 'learning';

      console.log(`Set learning rate to ${learningRate} for user ${user.uid}`);
    } catch (error) {
      console.error('Error setting learning rate:', error);
      throw error;
    }
  }

  /**
   * Persist the learningPhase field to Firestore.
   */
  async saveLearningPhase(phase: 'estimation' | 'learning' | 'review'): Promise<void> {
    const user = this.authService.currentUserValue;
    if (!user || !user.uid) return;

    const userDocRef = doc(this.firestore, `users/${user.uid}`);
    await updateDoc(userDocRef, { learningPhase: phase });
    user.learningPhase = phase;
    console.log(`Persisted learningPhase: ${phase}`);
  }

  /**
   * Save the model parameters (a and b) for the user in Firestore
   */
  async saveModelParams(a: number, b: number, frontier: number): Promise<void> {
    const user = this.authService.currentUserValue;
    if (!user || !user.uid) {
      throw new Error('No user logged in');
    }

    try {
      // Initialize modelParams if it doesn't exist
      if (!user.modelParams) {
        user.modelParams = { a: 0, b: 0, frontier: 0 };
      }

      // Update the model parameters
      user.modelParams.a = a;
      user.modelParams.b = b;
      user.modelParams.frontier = frontier;

      // Update the user document with the model parameters
      const userDocRef = doc(this.firestore, `users/${user.uid}`);
      await updateDoc(userDocRef, {
        modelParams: user.modelParams
      });

      console.log(`Saved model parameters a=${a}, b=${b}, frontier=${frontier} for user ${user.uid}`);
    } catch (error) {
      console.error('Error saving model parameters:', error);
      throw error;
    }
  }

  /**
   * Set the session queue by copying the current mastery queue
   */
  async setSessionQueue(): Promise<void> {
    const user = this.authService.currentUserValue;
    if (!user || !user.uid) {
      throw new Error('No user logged in');
    }

    try {
      // Copy masteryQueue to sessionQueue
      user.sessionQueue = user.masteryQueue ? [...user.masteryQueue] : [];

      // Update the user document with the session queue
      const userDocRef = doc(this.firestore, `users/${user.uid}`);
      await updateDoc(userDocRef, {
        sessionQueue: user.sessionQueue
      });

      console.log(`Set session queue with ${user.sessionQueue.length} words from mastery queue`);
    } catch (error) {
      console.error('Error setting session queue:', error);
      throw error;
    }
  }

  /**
   * Add a word to the session queue and persist to Firestore.
   */
  async addToSessionQueue(lemmaId: string): Promise<void> {
    const user = this.authService.currentUserValue;
    if (!user || !user.uid) return;

    if (!user.sessionQueue) {
      user.sessionQueue = [];
    }
    if (!user.sessionQueue.includes(lemmaId)) {
      user.sessionQueue.push(lemmaId);
    }

    const userDocRef = doc(this.firestore, `users/${user.uid}`);
    await updateDoc(userDocRef, { sessionQueue: user.sessionQueue });
    console.log(`Added ${lemmaId} to session queue (${user.sessionQueue.length} total)`);
  }

  /**
   * Remove a word from the mastery queue and persist to Firestore.
   */
  async removeFromMasteryQueue(lemmaId: string): Promise<void> {
    const user = this.authService.currentUserValue;
    if (!user || !user.uid || !user.masteryQueue) return;

    const idx = user.masteryQueue.indexOf(lemmaId);
    if (idx !== -1) {
      user.masteryQueue.splice(idx, 1);
      const userDocRef = doc(this.firestore, `users/${user.uid}`);
      await updateDoc(userDocRef, { masteryQueue: user.masteryQueue });
      console.log(`Removed ${lemmaId} from mastery queue (${user.masteryQueue.length} remaining)`);
    }
  }

  /**
   * Remove a word from the session queue and persist to Firestore.
   */
  async removeFromSessionQueue(lemmaId: string): Promise<void> {
    const user = this.authService.currentUserValue;
    if (!user || !user.uid || !user.sessionQueue) return;

    const idx = user.sessionQueue.indexOf(lemmaId);
    if (idx !== -1) {
      user.sessionQueue.splice(idx, 1);
      const userDocRef = doc(this.firestore, `users/${user.uid}`);
      await updateDoc(userDocRef, { sessionQueue: user.sessionQueue });
      console.log(`Removed ${lemmaId} from session queue (${user.sessionQueue.length} remaining)`);
    }
  }

  /**
   * Save an attempt record to Firestore
   * @param attempt The attempt record to save
   */
  async saveAttempt(attempt: Attempt): Promise<void> {
    const user = this.authService.currentUserValue;
    if (!user || !user.uid) {
      throw new Error('No user logged in');
    }

    try {
      // Save to Firestore with auto-generated ID
      const attemptsCollection = collection(this.firestore, `users/${user.uid}/attempts`);
      await addDoc(attemptsCollection, attempt);

      console.log(`Saved attempt for lemma ${attempt.lemmaId}: ${attempt.response} (${attempt.questionType}) in ${attempt.answerTime}ms`);
    } catch (error) {
      console.error('Error saving attempt:', error);
      throw error;
    }
  }

  /**
   * Save or update a session record in Firestore under users/{uid}/sessions/{sessionId}
   * Uses setDoc with merge:true so it can be called incrementally throughout the session.
   */
  async saveSession(sessionId: string, session: Partial<Session>): Promise<void> {
    const user = this.authService.currentUserValue;
    if (!user || !user.uid || !sessionId) return;

    try {
      const sessionRef = doc(this.firestore, `users/${user.uid}/sessions/${sessionId}`);
      await setDoc(sessionRef, session, { merge: true });
    } catch (error) {
      console.error('Error saving session:', error);
    }
  }

  /**
   * Load all sessions for the current user from the past N days.
   */
  async getRecentSessions(days: number): Promise<Session[]> {
    const user = this.authService.currentUserValue;
    if (!user || !user.uid) return [];

    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - days);

    const sessionsRef = collection(this.firestore, `users/${user.uid}/sessions`);
    const q = query(sessionsRef, where('beginSessionTime', '>=', cutoff));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(d => d.data() as Session);
  }

  /**
   * Update a lemma state in Firestore
   * @param lemmaState The lemma state to update
   */
  async updateLemmaState(lemmaState: LemmaState): Promise<void> {
    const user = this.authService.currentUserValue;
    if (!user || !user.uid) {
      throw new Error('No user logged in');
    }

    try {
      const lemmaStateRef = doc(this.firestore, `users/${user.uid}/lemmaStates/${lemmaState.lemmaId}`);
      await setDoc(lemmaStateRef, {
        lemmaId: lemmaState.lemmaId,
        rank: lemmaState.rank,
        wordStatus: lemmaState.wordStatus,
        repetitions: lemmaState.repetitions,
        lapses: lemmaState.lapses,
        consecutiveCorrect: lemmaState.consecutiveCorrect,
        consecutiveLapses: lemmaState.consecutiveLapses,
        attemptsInSession: lemmaState.attemptsInSession,
        knowledgeProb: lemmaState.knowledgeProb,
        sessionKnowledgeProb: lemmaState.sessionKnowledgeProb,
        easeFactor: lemmaState.easeFactor,
        interval: lemmaState.interval,
        dueDate: lemmaState.dueDate,
        ...(lemmaState.definition !== undefined && { definition: lemmaState.definition }),
        ...(lemmaState.examples !== undefined && { examples: lemmaState.examples }),
        updatedAt: serverTimestamp()
      }, { merge: true });
      console.log(`Updated lemma state for ${lemmaState.lemmaId}`);
    } catch (error) {
      console.error('Error updating lemma state:', error);
      throw error;
    }
  }

  /**
   * Update only the SRS scheduling fields for a lemma state in Firestore.
   * Called after SM2 recalculation to persist interval, easeFactor, dueDate, and repetitions.
   */
  async updateLemmaScheduling(lemmaState: LemmaState): Promise<void> {
    const user = this.authService.currentUserValue;
    if (!user || !user.uid) {
      throw new Error('No user logged in');
    }

    try {
      const lemmaStateRef = doc(this.firestore, `users/${user.uid}/lemmaStates/${lemmaState.lemmaId}`);
      await updateDoc(lemmaStateRef, {
        interval: lemmaState.interval,
        easeFactor: lemmaState.easeFactor,
        dueDate: lemmaState.dueDate,
        repetitions: lemmaState.repetitions,
        updatedAt: serverTimestamp()
      });
      console.log(`Updated scheduling for ${lemmaState.lemmaId}: interval=${lemmaState.interval}d, ease=${lemmaState.easeFactor.toFixed(2)}, reps=${lemmaState.repetitions}`);
    } catch (error) {
      console.error(`Error updating lemma scheduling for ${lemmaState.lemmaId}:`, error);
      throw error;
    }
  }
}
